import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import type {
  ReferralOrderProvider,
  ReferralOrderProvidersResponse,
  ReferralOrderProviderRequest,
  ReferralProviderSearchResult,
  ReferralProviderSearchResponse,
  ReferralOrderProviderSearchParams,
  ReferralProviderDetailResult,
  ReferralProviderPricingRequest,
  ReferralProviderPricingResponse,
} from './types'

/**
 * Serializes provider search params to a URL query string.
 * Uses loose `!= null` checks so both `null` and `undefined` are treated as "omit param".
 * Returns an empty string when no params produce output.
 */
export function buildReferralProviderSearchQueryString(
  params: ReferralOrderProviderSearchParams & { cursor?: string | null },
): string {
  const searchParams = new URLSearchParams()
  // Uses != null (loose) throughout — treats both undefined and null as "omit param"

  if (params.use_patient_preferences != null)
    searchParams.set('use_patient_preferences', String(params.use_patient_preferences))
  if (params.provider_ids?.length)
    searchParams.set('provider_ids', params.provider_ids.join(','))
  if (params.search) searchParams.set('search', params.search)
  if (params.priority != null) searchParams.set('priority', params.priority)
  if (params.max_distance_miles != null)
    searchParams.set('max_distance_miles', String(params.max_distance_miles))
  if (params.zip_code) searchParams.set('zip_code', params.zip_code)
  if (params.gender != null) searchParams.set('gender', params.gender)
  if (params.languages?.length)
    searchParams.set('languages', params.languages.join(','))
  if (params.in_network != null) searchParams.set('in_network', String(params.in_network))
  if (params.max_wait_days != null)
    searchParams.set('max_wait_days', String(params.max_wait_days))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  if (params.include_pricing != null)
    searchParams.set('include_pricing', String(params.include_pricing))
  if (params.cursor) searchParams.set('cursor', params.cursor)

  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}

export type ReferralProviderSearchQueryArg = ReferralOrderProviderSearchParams & {
  orderId: string
}

export const providersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listReferralOrderProviders: builder.query<ReferralOrderProvider[], { orderId: string }>({
      query: ({ orderId }) => apiEndpoints.referralOrders.providers(orderId),
      transformResponse: (response: ReferralOrderProvidersResponse) => response.providers,
      providesTags: (_result, _error, { orderId }) => [
        { type: 'ReferralOrderProvider', id: `LIST-${orderId}` },
      ],
    }),

    getReferralOrderProvider: builder.query<
      ReferralOrderProvider,
      { orderId: string; id: string }
    >({
      query: ({ orderId, id }) => apiEndpoints.referralOrders.provider(orderId, id),
      providesTags: (_result, _error, { id }) => [
        { type: 'ReferralOrderProvider', id },
      ],
    }),

    addReferralOrderProvider: builder.mutation<
      ReferralOrderProvider,
      { orderId: string } & ReferralOrderProviderRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: apiEndpoints.referralOrders.providers(orderId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'ReferralOrderProvider', id: `LIST-${orderId}` },
        { type: 'ReferralOrder', id: orderId },
      ],
    }),

    updateReferralOrderProvider: builder.mutation<
      ReferralOrderProvider,
      { orderId: string; id: string } & ReferralOrderProviderRequest
    >({
      query: ({ orderId, id, ...body }) => ({
        url: apiEndpoints.referralOrders.provider(orderId, id),
        method: 'PATCH',
        body,
      }),
      // Optimistically apply the rank/practice change to the cached order list so
      // drag-and-drop reranking reflects immediately; roll back if the PATCH fails.
      async onQueryStarted({ orderId, id, provider }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          providersApi.util.updateQueryData('listReferralOrderProviders', { orderId }, (draft) => {
            const target = draft.find((candidate) => candidate.id === id)
            if (target) Object.assign(target, provider)
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      // Deliberately does NOT invalidate the order provider list. Reranking issues
      // several PATCHes in parallel; a per-call list invalidation would refetch a
      // partially-updated order mid-flight and clobber the optimistic cache. The
      // onQueryStarted patch keeps the cache correct, and callers refetch once if a
      // call fails. The individual provider tag is still invalidated for detail views.
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ReferralOrderProvider', id },
      ],
    }),

    removeReferralOrderProvider: builder.mutation<void, { orderId: string; id: string }>({
      // The backend answers a successful provider delete with 204 No Content. The
      // shared `/api/*` proxy cannot rebuild a 204 (the Response constructor rejects
      // a body on a null-body status) and instead surfaces an empty-body 500. Treat
      // that specific signature as the success it represents; defer every other error.
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: apiEndpoints.referralOrders.provider(arg.orderId, arg.id),
          method: 'DELETE',
        })
        if (result.error) {
          const { status, data } = result.error as { status?: number | string; data?: unknown }
          const isProxiedNoContent = status === 500 && (data == null || data === '')
          if (isProxiedNoContent) return { data: undefined }
          return { error: result.error }
        }
        return { data: undefined }
      },
      // Optimistically drop the provider from the cached order list; restore it if
      // the DELETE fails.
      async onQueryStarted({ orderId, id }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          providersApi.util.updateQueryData('listReferralOrderProviders', { orderId }, (draft) =>
            draft.filter((candidate) => candidate.id !== id),
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (_result, _error, { orderId, id }) => [
        { type: 'ReferralOrderProvider', id },
        { type: 'ReferralOrderProvider', id: `LIST-${orderId}` },
        { type: 'ReferralOrder', id: orderId },
      ],
    }),

    searchReferralOrderProviders: builder.infiniteQuery<
      ReferralProviderSearchResponse,
      ReferralProviderSearchQueryArg,
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const { orderId, ...params } = queryArg
        const qs = buildReferralProviderSearchQueryString({
          ...params,
          cursor: pageParam ?? undefined,
        })
        return `${apiEndpoints.referralOrders.providerSearch(orderId)}${qs}`
      },
      providesTags: (result, _error, { orderId }) =>
        result
          ? result.pages.flatMap((page: ReferralProviderSearchResponse) =>
              page.data.map((p: ReferralProviderSearchResult) => ({
                type: 'ReferralOrderProvider' as const,
                id: `search-${orderId}-${p.provider_id}`,
              })),
            )
          : [],
    }),

    /** Fetches enriched provider detail in the context of a referral order. */
    getProviderDetail: builder.query<
      ReferralProviderDetailResult,
      { orderId: string; providerId: string; zipCode?: string; includePricing?: boolean }
    >({
      query: ({ orderId, providerId, zipCode, includePricing }) => {
        const searchParams = new URLSearchParams()
        if (zipCode) searchParams.set('zip_code', zipCode)
        if (includePricing != null) searchParams.set('include_pricing', String(includePricing))
        const qs = searchParams.toString()
        return `${apiEndpoints.referralOrders.providerSearchDetail(orderId, providerId)}${qs ? `?${qs}` : ''}`
      },
      providesTags: (_result, _error, { providerId }) => [
        { type: 'ReferralOrderProvider', id: `detail-${providerId}` },
      ],
    }),

    batchReferralOrderProviderPricing: builder.mutation<
      ReferralProviderPricingResponse,
      { orderId: string } & ReferralProviderPricingRequest
    >({
      query: ({ orderId, providers, zip_code }) => ({
        url: apiEndpoints.referralOrders.providerPricing(orderId),
        method: 'POST',
        body: { providers, ...(zip_code !== undefined ? { zip_code } : {}) },
      }),
      invalidatesTags: [],
    }),
  }),
  overrideExisting: false,
})

export const {
  useListReferralOrderProvidersQuery,
  useGetReferralOrderProviderQuery,
  useAddReferralOrderProviderMutation,
  useUpdateReferralOrderProviderMutation,
  useRemoveReferralOrderProviderMutation,
  useSearchReferralOrderProvidersInfiniteQuery,
  useGetProviderDetailQuery,
  useBatchReferralOrderProviderPricingMutation,
} = providersApi
